import React from 'react';
import { ChevronDown } from 'lucide-react';
import { useDropdown } from '../../hooks/useOutsideClick';

const LANGUAGES = [
  { code: 'en-us', label: 'English (US)', flag: '🇺🇸' },
  { code: 'en-gb', label: 'English (UK)', flag: '🇬🇧' },
  { code: 'es', label: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', label: 'French', flag: '🇫🇷' },
  { code: 'de', label: 'German', flag: '🇩🇪' },
  { code: 'hi', label: 'Hindi', flag: '🇮🇳' },
  { code: 'pt', label: 'Portuguese', flag: '🇵🇹' },
  { code: 'zh', label: 'Chinese (Mandarin)', flag: '🇨🇳' },
  { code: 'ja', label: 'Japanese', flag: '🇯🇵' },
];

function LanguageOption({ lang, selected, onSelect }) {
  return (
    <button
      onClick={onSelect}
      className={`w-full px-4 py-2.5 flex items-center gap-2 text-left text-[13px] hover:bg-[#F1ECFE] ${
        selected ? 'bg-[#F5F1FE] text-[#5B4FE8] font-semibold' : 'text-[#1A1533]'
      }`}
    >
      <span className="text-base">{lang.flag}</span>
      {lang.label}
    </button>
  );
}

function LanguageDropdown({ value, onChange }) {
  const { open, toggle, close, ref } = useDropdown();
  const current = LANGUAGES.find((l) => l.code === value) || LANGUAGES[0];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-3 py-3 rounded-xl border border-[#ECE6FA] bg-white text-[13.5px] hover:border-[#C7BFF5]"
      >
        <span className="flex items-center gap-2">
          <span className="text-lg">{current.flag}</span>
          <span className="font-medium text-[#1A1533]">{current.label}</span>
        </span>
        <ChevronDown size={15} className={`text-[#5B4FE8] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 bg-white border border-[#ECE6FA] rounded-xl shadow-lg max-h-[280px] overflow-y-auto zara-scroll py-1">
          {LANGUAGES.map((l) => (
            <LanguageOption
              key={l.code}
              lang={l}
              selected={l.code === value}
              onSelect={() => { onChange(l.code); close(); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function LanguageAssessmentPanel({ module, onChange }) {
  return (
    <div>
      <h3 className="text-[17px] font-bold text-[#1A1533]">Language assessment</h3>
      <p className="text-[13px] text-[#4A4463] mt-2 leading-relaxed">
        Select a language to assess the candidate's fluency and add context if you'd like to focus on specific skills or topics.
      </p>
      <p className="text-[12.5px] text-[#6B6485] mt-3 leading-relaxed">
        <span className="font-semibold text-[#1A1533]">Note:</span> When Language Assessment is added, "User selected language" won't be available as an interview language option.
      </p>

      <div className="mt-6">
        <label className="text-[13px] font-semibold text-[#1A1533]">Select a language</label>
        <div className="mt-2">
          <LanguageDropdown
            value={module.language || 'en-us'}
            onChange={(code) => onChange({ language: code })}
          />
        </div>
      </div>

      <div className="mt-5">
        <label className="text-[13px] font-semibold text-[#1A1533]">Enter more details to tailor the assessment (Optional)</label>
        <textarea
          value={module.details || ''}
          onChange={(e) => onChange({ details: e.target.value })}
          placeholder={`e.g. Evaluate Spanish fluency for a customer support role, focusing on conversational tone and problem-solving communication.`}
          rows={5}
          className="mt-2 w-full px-3 py-2.5 rounded-xl border border-[#ECE6FA] bg-white text-[13px] placeholder-[#9A93B0] outline-none focus:ring-2 focus:ring-[#5B4FE8]/30 resize-none"
        />
      </div>
    </div>
  );
}
